import { LabelVariant } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';

import {
  ACTION_STEP_TERMINAL_STATUSES,
  ActionStepStatus,
  ExecutionStepStatus,
} from 'constants/aiChat';

import {
  ActionStepToolIconMap,
  ActionStepTypeCompletedLabelMap,
  ActionStepTypeIconMap,
  ActionStepTypeLabelMap,
  ActionStepTypeLabelVariantMap,
  DEFAULT_ACTION_STEP_ICON,
} from './ActionStep.constants';
import { STATUS_LINE_LABELS } from './components/StatusLine/StatusLine.constants';

export const isTextContentProps = props =>
  !_.isNil(props) && _.isObject(props) && _.isString(props.text) && !!props.text.length;

export const isConfirmActionsContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  _.isString(props.submitLabel) &&
  !!props.submitLabel.length &&
  _.isString(props.cancelLabel) &&
  !!props.cancelLabel.length &&
  _.isFunction(props.onSubmit) &&
  _.isFunction(props.onCancel);

export const isErrorContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  _.isString(props.errorMessage) &&
  !!props.errorMessage.length;

export const isListContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  Array.isArray(props.items) &&
  !!props.items.length &&
  props.items.every(item => _.isObject(item) && _.isString(item.id) && _.isString(item.text));

export const isStepSummaryContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  (_.isNil(props.label) || _.isString(props.label)) &&
  _.isString(props.summary) &&
  !!props.summary.length;

export const isScannedSectionsContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  Array.isArray(props.scannedSections) &&
  props.scannedSections.every(
    s => _.isObject(s) && _.isString(s.source) && _.isString(s.sectionNumber),
  );

export const isToolCallsContentProps = props =>
  !_.isNil(props) &&
  _.isObject(props) &&
  Array.isArray(props.toolCalls) &&
  !!props.toolCalls.length &&
  props.toolCalls.every(call => _.isObject(call) && _.isString(call.id) && _.isString(call.label));

export const isActionStepStatusActive = status => !ACTION_STEP_TERMINAL_STATUSES.has(status);

export const getAutoExpandedForStatus = status => {
  if (status === ActionStepStatus.RUNNING || status === ActionStepStatus.STREAMING) {
    return true;
  }

  if (status === ActionStepStatus.ERROR) {
    return true;
  }

  return false;
};

export const resolveActionStepIcon = (toolName, actionStepType) => {
  const toolIcon = _.isString(toolName) ? ActionStepToolIconMap[toolName] : null;

  return toolIcon ?? ActionStepTypeIconMap[actionStepType] ?? DEFAULT_ACTION_STEP_ICON;
};

// DEV-2939: resolves the type chip's text + color for a step row. The chip
// is normally type-derived ("Running" purple for EXECUTING_COMMAND), but it
// must react to status so a finished row doesn't read as still in-flight:
//   - AWAITING_CONFIRMATION -> "Awaiting" / ORANGE (DEV-2913)
//   - SUCCESS               -> past-tense verb ("Ran") / GRAY
//   - SKIPPED               -> "Skipped" / GRAY (DEV-2951) — the model never
//                              ran this step, so the type verb ("Reading")
//                              would misrepresent it as work that happened.
//   - CANCELLED             -> present verb / GRAY (never ran to completion)
//   - active / pending      -> present verb / type color (unchanged)
// text may be undefined when actionStepType is null/unmapped; the caller
// gates rendering on that.
export const resolveActionStepTypeLabel = (status, actionStepType) => {
  if (status === ActionStepStatus.AWAITING_CONFIRMATION) {
    return { text: STATUS_LINE_LABELS.AWAITING_CONFIRMATION, variant: LabelVariant.ORANGE };
  }

  if (status === ActionStepStatus.SUCCESS) {
    return {
      text:
        ActionStepTypeCompletedLabelMap[actionStepType] ?? ActionStepTypeLabelMap[actionStepType],
      variant: LabelVariant.GRAY,
    };
  }

  if (status === ActionStepStatus.SKIPPED) {
    return { text: STATUS_LINE_LABELS.SKIPPED, variant: LabelVariant.GRAY };
  }

  if (status === ActionStepStatus.CANCELLED) {
    return { text: ActionStepTypeLabelMap[actionStepType], variant: LabelVariant.GRAY };
  }

  return {
    text: ActionStepTypeLabelMap[actionStepType],
    variant: ActionStepTypeLabelVariantMap[actionStepType],
  };
};

export const mapActionStepStatusToExecutionStepStatus = status => {
  switch (status) {
    case ActionStepStatus.PENDING:
    case ActionStepStatus.STARTING:
      return ExecutionStepStatus.PENDING;
    case ActionStepStatus.RUNNING:
    case ActionStepStatus.STREAMING:
    case ActionStepStatus.AWAITING_CONFIRMATION:
      // DEV-2913: AWAITING_CONFIRMATION is an active state from the
      // execution-tracker perspective (the step is mid-flight, just
      // paused on a user click). Surfaces as EXECUTING.
      return ExecutionStepStatus.EXECUTING;
    case ActionStepStatus.SUCCESS:
      return ExecutionStepStatus.COMPLETED;
    case ActionStepStatus.ERROR:
      return ExecutionStepStatus.FAILED;
    case ActionStepStatus.CANCELLED:
      return ExecutionStepStatus.CANCELLED;
    case ActionStepStatus.SKIPPED:
      // DEV-2951: distinct from PENDING so the progress indicator renders a
      // muted "skipped" glyph instead of the hollow pending circle.
      return ExecutionStepStatus.SKIPPED;
    default:
      return ExecutionStepStatus.PENDING;
  }
};
